import Vue from 'vue'
import Vuex, {ActionContext, Store} from 'vuex'
import storage from '@/lib/storage'
import profile from '@/store/modules/profile'
import wallet from '@/store/modules/wallet'
import subscriptions from '@/store/modules/subscriptions'
import booking from '@/store/modules/booking'
import vehicle from '@/store/modules/vehicle'
import topbar from '@/store/modules/topbar'
import clientTypes from '@/store/modules/clientTypes'
import licenseTypes from '@/store/modules/licenseTypes'
import termOfServices from '@/store/modules/termOfServices'
import reportings from '@/store/modules/reportings'
import sdk from '@/lib/kepler/sdk'
import configuration from '@/store/modules/configuration'
import timestamps from '@/store/modules/timestamps'
import registration from '@/store/modules/registration'
import flowOutputs from '@/store/modules/flowOutputs'
import flowInputs from '@/store/modules/flowInputs'
import flowPersistent from '@/store/modules/flowPersistent'
import accounts from '@/store/modules/accounts'
import filters from '@/store/modules/map'

Vue.use(Vuex)

export class PopupState {
  [key: string]: any

  public popup?: any | null
  public popupProps: any = null
  public popupTitle!: string | null
  public hideTopbar!: boolean

  // TODO> remove null here 👇 and see what happens
  constructor(popup: any | null, popupProps: any, popupTitle: string | null = null, hideTopbar: boolean = false) {
    this.popup = popup
    this.popupProps = popupProps
    this.popupTitle = popupTitle
    this.hideTopbar = hideTopbar
  }
}

export class DialogState extends PopupState {
  public emitConfirm: boolean = false

  public readonly defaultDialogConfirmedEvent: string = 'dialogConfirm'
  public dialogConfirmedEvent: string = 'dialogConfirm'

  constructor(
    // popup arguments
    popup: any | null, popupProps: any, popupTitle: string | null = null, hideTopbar: boolean = false,
    // dialogs events
    dialogConfirmedEvent?: string | null,
  ) {
    super(popup, popupProps, popupTitle, hideTopbar)

    if (typeof dialogConfirmedEvent === 'string') {
      this.dialogConfirmedEvent = dialogConfirmedEvent
    }
  }

  public isDefaultConfirm() {
    return this.dialogConfirmedEvent === this.defaultDialogConfirmedEvent
  }
}

export class RootState {
  [key: string]: any // modules typings don't get picked up by compiler, meh.
  public userToken!: string
  public popupState: PopupState[] = []
  public dialogState: DialogState[] = []
  public logs: string[] = []
}

const rootState: Store<RootState> = new Vuex.Store({
  state: new RootState(),
  mutations: {
    INIT_ROOT_STATE(state) {
      storage.restore('root', state)
      state.popupState = []
      state.dialogState = []
    },
    UPDATE_USER_TOKEN(state, payload) {
      Vue.set(state, 'userToken', payload)
    },
    OPEN_POPUP(state, payload: PopupState) {
      state.popupState.push(payload)
    },
    CLOSE_POPUP(state, index?: number) {
      if (index) {
        state.popupState.splice(index, 1)
      } else {
        state.popupState.pop()
      }
    },
    OPEN_DIALOG(state, payload: DialogState) {
      const lastIndex = state.dialogState.length - 1
      // quick and dirty check if dialog is identical to last one
      if (JSON.stringify(payload) !== JSON.stringify(state.dialogState[lastIndex])) {
        state.dialogState.push(payload)
      }
    },
    CLOSE_DIALOG(state, index?: number) {
      if (typeof index === 'number') {
        state.dialogState.splice(index, 1)
      } else {
        state.dialogState.pop()
      }
    },
    FLUSH_EMPTY_QUEUE(state) {
      state.popupState = []
      state.dialogState = []
    },
    FLUSH_POPUP_QUEUE(state) {
      state.popupState = []
    },
    FLUSH_DIALOG_QUEUE(state) {
      state.dialogState = []
    },
    LOG(state, payload: string) {
      const maxLength = 100 // adjust as needed
      state.logs.push(payload)
      while (state.logs.length > maxLength) {
        state.logs.shift()
      }
    },
    CLEAR_LOGS(state) {
      state.logs.splice(0)
    },
  },
  actions: {
    init(store: ActionContext<RootState, any>) {
      store.commit('INIT_ROOT_STATE')
    },
    sleep({state}) {
      const o = Object.assign({}, state)
      o.popupState = []
      o.dialogState = []
      storage.save('root', o)
    },
    purge() {
      storage.delete('root')
    },
    updateUserToken(store: ActionContext<RootState, any>, payload) {
      store.commit('UPDATE_USER_TOKEN', payload)
      store.dispatch('sleep')
    },
    openPopup({commit, dispatch}, popup: PopupState) {
      commit('OPEN_POPUP', popup)
      dispatch('sleep')
    },
    closePopup(store: ActionContext<RootState, any>, index?: number) {
      store.commit('CLOSE_POPUP', index)
    },
    openDialog({commit, dispatch}, dialog: DialogState) {
      commit('OPEN_DIALOG', dialog)
      dispatch('sleep')
    },
    closeDialogNoEvents(store: ActionContext<RootState, any>, index?: number) {
      store.commit('CLOSE_DIALOG', index)
    },
    closeDialog(store: ActionContext<RootState, any>, index?: number) {
      store.commit('CLOSE_DIALOG', index)
    },
    flushConnectionErrors(store: ActionContext<RootState, any>) {
      // a bit hacky
      for (const [i, d] of store.state.dialogState.entries()) {
        if (d.popupProps.title === 'common.you-are-offline') {
          store.commit('CLOSE_DIALOG', i)
        }
      }
    },
    cancelDialog(store: ActionContext<RootState, any>, index?: number) {
      store.commit('CLOSE_DIALOG', index)
    },
    flushEmptyPopups(store: ActionContext<RootState, any>) {
      store.commit('FLUSH_EMPTY_QUEUE')
    },
    closeOverlays(store: ActionContext<RootState, any>) {
      store.commit('FLUSH_POPUP_QUEUE')
      store.commit('FLUSH_DIALOG_QUEUE')
    },
    flushPopups(store: ActionContext<RootState, any>) {
      store.commit('FLUSH_POPUP_QUEUE')
    },
    globalNotes() {
      return sdk.people.globalNotes()
    },
    log(store: ActionContext<RootState, any>, payload?: string) {
      store.commit('LOG', payload)
    },
    clearLogs(store: ActionContext<RootState, any>) {
      store.commit('CLEAR_LOGS')
    },
  },
  getters: {
    popupIsOpen: (s) => s.popupState.length,
    dialogIsOpen: (s) => s.dialogState.length,
    isLogged: (s) => !!s.userToken,
  },
  modules: {
    configuration,
    profile,
    wallet,
    subscriptions,
    clientTypes,
    licenseTypes,
    termOfServices,
    booking,
    topbar,
    vehicle,
    reportings,
    timestamps,
    registration,
    flowInputs,
    flowOutputs,
    flowPersistent,
    accounts,
    filters,
  },
})

export default rootState
